import React from 'react';
import { SoloTemplate } from '@/types/soloChoreographer';
import { SOLO_INSTRUMENT_LABELS } from '@/types/soloChoreographer';
import { Guitar, Check, Gauge, BarChart3 } from 'lucide-react';
import { Badge } from '../../../components/ui';

interface SoloTemplateCardProps {
  template: SoloTemplate;
  isSelected?: boolean;
  onClick?: () => void;
}

const SoloTemplateCard: React.FC<SoloTemplateCardProps> = ({
  template,
  isSelected = false,
  onClick,
}) => {
  const avgIntensity = Math.round(
    template.phrases.reduce((sum, p) => sum + p.intensity, 0) / template.phrases.length
  );

  const roles = template.phrases.map(p => p.role);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        relative text-left w-full p-4 rounded-2xl border-2 transition-all
        hover:border-accent/50 hover:bg-slate-800/40 active:scale-[0.98]
        ${isSelected
          ? 'border-accent bg-accent/5 shadow-lg shadow-amber-500/10'
          : 'border-slate-800 bg-slate-950/40'
        }
      `}
    >
      {/* Selected check badge */}
      {isSelected && (
        <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-accent flex items-center justify-center text-slate-950">
          <Check size={14} strokeWidth={3} />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-accent/10 rounded-lg text-accent flex-shrink-0">
          <Guitar size={18} />
        </div>
        <div className="min-w-0">
          <h4 className="text-sm font-black text-slate-100 uppercase tracking-tight truncate">{template.name}</h4>
        </div>
      </div>

      {/* Description */}
      <p className="text-[11px] text-slate-400 leading-relaxed italic line-clamp-2 mb-3">"{template.description}"</p>

      {/* Metadata row */}
      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-800/60">
        {/* Length */}
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-slate-400">
            <BarChart3 size={10} />
            <span className="text-[11px] font-black uppercase tracking-widest">Length</span>
          </div>
          <span className="text-[11px] text-slate-300 font-bold block">{template.length} bars</span>
        </div>

        {/* Instrument */}
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-slate-400">
            <Guitar size={10} />
            <span className="text-[11px] font-black uppercase tracking-widest">Default</span>
          </div>
          <span className="text-[11px] text-slate-300 font-bold block truncate">
            {SOLO_INSTRUMENT_LABELS[template.defaultInstrument]}
          </span>
        </div>

        {/* Avg Intensity */}
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-slate-400">
            <Gauge size={10} />
            <span className="text-[11px] font-black uppercase tracking-widest">Energy</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-accent/60 to-accent rounded-full"
                style={{ width: `${avgIntensity}%` }}
              />
            </div>
            <span className="text-[11px] text-slate-400 font-mono">{avgIntensity}</span>
          </div>
        </div>
      </div>

      {/* Phrase roles */}
      <div className="mt-2 pt-2 border-t border-slate-800/40">
        <div className="flex flex-wrap gap-1">
          {roles.map((role, i) => (
            <Badge key={`${role}-${i}`} variant="info-muted" size="xs" pill className="font-bold capitalize">
              {role}
            </Badge>
          ))}
          <Badge variant="default-muted" size="xs" pill>
            {template.phrases.length} phrases
          </Badge>
        </div>
      </div>
    </button>
  );
};

export default SoloTemplateCard;
